import Velocity from 'velocity-animate';
import { TrackJS } from 'trackjs';

import Config from './config';
import optimizePath from './optimize-path';

import OpenXEnvironment from '@module/openx/environment';

let firstWallpaper = true;

export default class Media {
  constructor(meta) {
    // Sometimes things go wrong, revert to default in those cases
    if (meta === false || typeof meta === 'undefined' || !meta.src) {
      return Media.getFallback();
    }

    this.file = optimizePath(meta);
    this.meta = meta.meta || {};
    this.href = meta.href || false;
    this.title = meta.title || false;
    this.type = this.determineType(meta.src);
    this.timer = meta.timer || false;
    this.visibility = meta.visibility || false;
    this.node = false; // will contain HTML Element once preloaded
    this.naturalWidth = 0;
    this.naturalHeight = 0;

    const tags = {
      file: this.file,
    };

    if (this.meta.id) {
      tags['advert'] = this.meta.id;
      TrackJS.addMetadata('advert_tag', this.meta.id);
    }

    // Attempting to load an unsupported wallpaper type? No.
    if (this.type === 'unsupported') {
      TrackJS.track({
        name: 'UnsupportedWallpaper',
        message: 'Somehow a wallpaper that we not support got loaded - uh-oh.',
        stack: 'component/wallpaper/media',
      });

      return false;
    }

    // If the path starts with http:// while top frame is https:// it's considered unsafe
    if (top.location.protocol === 'https:' && this.file.match(/^http:/)) {
      TrackJS.track({
        name: 'InsecureWallpaper',
        message:
          'Somehow an insecure wallpaper attempted to load, this should NEVER happen.',
        stack: 'component/wallpaper/media',
      });

      return Media.getFallback();
    }

    // Generate the full path if the filename is not prefixed
    // with a protocol.
    if (this.file.match(/^https?:/)) {
      this.fullPath = this.file;
    } else {
      this.fullPath = `${Config.host}/${this.file}`;
    }
  }

  // Return the Fallback Media object
  static getFallback() {
    return new Media(Config.defaultMedia);
  }

  // Inject the media node into parent
  appendTo(parent) {
    if (!this.node) {
      return;
    }
    parent.appendChild(this.node);
  }

  // Set the HTMLElement as `.node` and get the naturalHeight/Width
  // if available. This needs to change for multiple file types.
  setNode(node) {
    this.node = node;
    this.naturalHeight = node.naturalHeight || node.height;
    this.naturalWidth = node.naturalWidth || node.width;
  }

  // Set the click for this item
  setClick(action) {
    if (!this.node || !action) {
      return;
    } // Nothing to do

    this.node.style.cursor = 'pointer';
    if (typeof action === 'string') {
      this.node.onclick = (e) => {
        e.preventDefault();
        const link = window.open(action);
        if (link) {
          link.opener = null;
        }
      };
    } else {
      this.node.onclick = action;
    }
  }

  // Remove the node from the DOM.
  remove() {
    if (!this.node) {
      return;
    }
    const parent = this.node.parentNode;
    if (parent) {
      parent.removeChild(this.node);
    }
    this.node = null;
  }

  // Fadeout based on ./config, returns a promise
  // to be used in an async function.
  hide() {
    if (!this.node) return Promise.resolve();
    if (this.type === 'unsupported') return Promise.resolve();

    return new Promise((complete) => {
      Velocity(
        this.node,
        { opacity: 0 },
        {
          ...Config.transition,
          complete,
        }
      );
    });
  }

  // Fadein based on ./config, returns a promise
  // to be used in an async function.
  show() {
    if (!this.node) return Promise.resolve();
    if (this.type === 'unsupported') return Promise.resolve();

    return new Promise((resolve) => {
      let delay = 0;

      // Only the first wallpaper will get a delay
      if (firstWallpaper) {
        delay = 400;
        firstWallpaper = false;
      }

      Velocity(
        this.node,
        { opacity: 1 },
        {
          ...Config.transition,
          delay,
          complete: resolve,
        }
      );
    });
  }

  // This determines the (probable) filetype for a file.
  determineType(filepath) {
    let extension = filepath
      .substr(filepath.lastIndexOf('.') + 1)
      .toLowerCase();
    [extension] = extension.split(/\?|#/);

    switch (extension) {
      case 'swf':
        return 'unsupported';

      case 'htm':
      case 'html':
        if (!OpenXEnvironment.supportsHTML()) {
          return 'unsupported';
        }

        return 'html5';
    }

    return 'image';
  }
}
